import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import type { PageVersion } from "@/types/database";

// page_versions rows are only ever written by the snapshot_page_version
// trigger (see the migration) — this hook only ever reads them, plus
// performs a restore (delete current blocks, re-insert the snapshot's
// blocks) using the exact same block-write RLS every other block edit
// goes through, not a special bypass.
export function usePageVersions(pageId: string | undefined) {
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["page-versions", pageId],
    enabled: Boolean(pageId),
    queryFn: async (): Promise<PageVersion[]> => {
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("page_versions" as any)
        .select("*")
        .eq("page_id", pageId as string)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PageVersion[];
    },
  });

  // Restores content only (block type/content/layout/visibility) — not the
  // page's own title/slug/SEO, which are structural rather than content
  // and could break an already-shared URL if silently reverted.
  const restore = useMutation({
    mutationFn: async (version: PageVersion): Promise<void> => {
      const supabase = await getClient();
      const { error: deleteError } = await supabase.from("blocks").delete().eq("page_id", version.page_id);
      if (deleteError) throw deleteError;

      if (version.blocks_snapshot.length > 0) {
        const { error: insertError } = await supabase.from("blocks").insert(
          version.blocks_snapshot.map((block) => ({
            page_id: version.page_id,
            block_type: block.block_type,
            content: block.content,
            block_order: block.block_order,
            row_order: block.row_order,
            col_span: block.col_span,
            visibility: block.visibility,
          })) as any
        );
        if (insertError) throw insertError;
      }
    },
    onSuccess: (_data, version) => {
      void queryClient.invalidateQueries({ queryKey: ["blocks", version.page_id] });
    },
  });

  return {
    versions: query.data ?? [],
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : restore.error instanceof Error ? restore.error.message : null,
    restoring: restore.isPending,
    restore: (version: PageVersion) => restore.mutateAsync(version),
  };
}
